import {
    useCallback,
    useMemo,
    useContext,
    useState,
    useRef,
} from 'react';
import { useTheme } from '@mui/material';
import { makeStyles, useMediaQuery, Button, Card, StatusLabel, Tooltip, Typography, CardContent } from '@ellucian/react-design-system/core';
import {
    spacing40,
    colorBackgroundDivider,
    spacing30,
    spacing50,
    colorTextAlertError,
    colorTextAlertSuccess,
    colorTextAlertWarning,
    colorTextNeutral500,
} from '@ellucian/react-design-system/core/styles/tokens';
import { useIntl } from 'react-intl';
import { CONSTANTS } from '../Helpers/Configuration';
import forOwn from 'lodash/forOwn';
import GlobalLocaleContext from '../GlobalLocale/GlobalLocaleContext';
import { useApi } from '../ApiContext/';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import { create as confettiCreate } from 'canvas-confetti';
import { SUPPORTED_FEATURES } from '@nsr/applicant-apply-shared/src/helpers/featureFlags';
import { Icon } from '@ellucian/ds-icons/lib';
import { FormattedMessage } from 'react-intl';
import CallToActionDialog from './CallToActionDialog';
import ApplicantCommentDialog from './ApplicantCommentDialog';

const useStyles = makeStyles({
    name: 'ApplicationCard'
})((theme) => ({
    applicationCardContainer: {
        height: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
        },
    },
    confetti: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none', // To allow interaction with other elements
    },
    completedContainer: {
        flexDirection: 'column',
        alignSelf: 'flex-start',
        textAlign: 'left',
        paddingRight: 0,
        [theme.breakpoints.up('md')]: {
            alignSelf: 'center',
            textAlign: 'center',
            paddingRight: spacing50,
        },
    },
    infoContainer: {
        flexDirection: 'column',
        paddingLeft: 0,
        [theme.breakpoints.up('md')]: {
            paddingLeft: spacing50,
        },
    },
    appStatusIcon:{
        fontWeight: 'bolder'                       
    },
    infoitemLabel: {
        width: 'fit-content',
        alignSelf: 'flex-start',
    },
    divider: {
        height: 1,
        width: '100%',
        margin: `${spacing30} 0`,
        background: colorBackgroundDivider,
        [theme.breakpoints.up('md')]: {
            width: 2,
            height: 'auto',
            margin: '6 0',
        },
    },
    mobileStatusRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        columnGap: spacing30,
        rowGap: spacing30,
    },
    mobileStatusLeft: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: spacing30,
        rowGap: spacing30,
        minWidth: 0,
    },
    desktopStatusRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: spacing30,
        rowGap: spacing30,
    },
    desktopStatusChips: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: spacing30,
        rowGap: spacing30,
        minWidth: 0,
    },
    mobilePrimaryButton: {
        marginTop: 0,
        minWidth: 'unset',
        width: 'fit-content',
        alignSelf: 'center',
    },
    tabletPrimaryButton: {
        marginTop: spacing50,
        minWidth: 'unset',
        width: '100%',
        alignSelf: 'stretch',
    },
    statusLabelItems:{
        width:'80px'
    },
    statusFont:{
        color: 'green', 
        fontWeight:'bolder'
    },
    academicContainer: {
        display: 'flex',
        flexDirection: 'row',
        margin: `${spacing30} 0`,
    },
    academicItems: {
        marginRight: spacing50,
    },
    buttonContainer: {
        display: 'flex',
        flex: 'auto',
        justifyContent: 'flex-end',
    },
    button: {
        height: 'fit-content',
        minWidth: 'unset',
        width: 'fit-content',
        alignSelf: 'flex-start',
        [theme.breakpoints.up('md')]: {
            width: 'fit-content',
            height: 'fit-content',
            marginRight: spacing30,
            alignSelf: 'center',
        },
    },
    mobileActionButton: {
        marginTop: spacing40,
        minWidth: 'unset',
    },
    standardField: {
        paddingBottom: spacing30,
    },
    lastAccessed: {
        paddingTop: spacing40,
    },
    buttonMargin:{
        marginTop:'20px'
    },
    submitStatus:{
        fontWeight: 'bolder',
        color: colorTextAlertSuccess 
    },
    incompleteInfoIcon: {
        marginLeft: spacing30,
        cursor: 'pointer',
        color: colorTextAlertError,
        verticalAlign: 'middle',
    },
}));
const ApplicationCard = (props) => {
    const { classes } = useStyles();
    const { formatMessage } = useIntl();
    const {
        application,
        accessApplication: accessApplicationProp,
        //withdrawApplication: withdrawApplicationProp
    } = props;
    const {
        status,
        name,
        pageStatuses,
        id,
        pageCount,
        defaultLanguage,
        archivedOn,
        academicTerm,
        academicProgram,
    } = application;
    const {featureSupported} = window.PRELOADED_STATE ?? {};
    //we need to make a boolean variable for showing the old submitted UI
    const enableDecision= featureSupported?.[SUPPORTED_FEATURES.applicationReview];
    const paymentStatus = application?.paymentInformation?.paymentStatus;
    const {
        applicationFormStatuses,
        applicationPageStatuses,
        applicationProcessingStatus,
        applicationPaymentInformation,
    } = CONSTANTS;
    const { globalLocale } = useContext(GlobalLocaleContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const isVerySmall = useMediaQuery(theme.breakpoints.down(360));
    const isStacked = useMediaQuery(theme.breakpoints.down('lg'));
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const canvasRef = useRef(null);
    const api = useApi();
    const snackbar = useSnackbar();
    const [isOngoingApiCall, setIsOngoingApiCall] = useState(false);
    const completedCount = useMemo(() => {
        let count = 0;
        forOwn(pageStatuses, (value) => {
            if (value === applicationPageStatuses.COMPLETED) {
                count++;
            }
        });
        return count;
    }, [pageStatuses]);
    const [isApplicantCommentDialogOpen, setIsApplicantCommentDialogOpen] = useState(false);
    const [applicantComment, setApplicantComment] = useState('');
    
    // Check if application has documents requiring clarification (INCOMPLETE status)
    const isIncomplete = status === applicationFormStatuses.INCOMPLETE;
    
    const appIsConsideredSubmitted = useMemo(() => {
        // INCOMPLETE status should NOT be considered submitted - applicant needs to take action
        if (isIncomplete) {
            return false;
        }
        //Consider an application submitted if it started but has a pending payment
        if (status === applicationFormStatuses.STARTED) {
            return (
                paymentStatus &&
        ![
            applicationPaymentInformation.paymentStatus.draft,
            applicationPaymentInformation.paymentStatus.successful,
        ].includes(paymentStatus)
            );
        } else {
            return [
                applicationFormStatuses.SUBMITTED,
                applicationProcessingStatus.personCheck,
                applicationProcessingStatus.personError,
                applicationProcessingStatus.pending,
                applicationProcessingStatus.onHold,
                applicationProcessingStatus.integrationError,
                applicationProcessingStatus.error,
                applicationProcessingStatus.complete,
                applicationProcessingStatus.fraud,
            ].includes(status);
        }
    }, [status, paymentStatus, isIncomplete]);

    const getLocalizedName = useCallback(() => {
        if(!name){
            return '';
        }
        return name[globalLocale] ?? name[defaultLanguage];
    }, [globalLocale, name, defaultLanguage]);
    const getActionCardButtonName = useMemo(() => {
        if (appIsConsideredSubmitted || archivedOn) {
            const buttonText = enableDecision ? <FormattedMessage id="applicantHomeReview" /> : <FormattedMessage id="applicantHomeView" />;
            return buttonText;
        }
        
        switch (status) {
            case applicationFormStatuses.NOTSTARTED:
                return <FormattedMessage id="applicantHomeStart" />;
            case applicationFormStatuses.STARTED:
            case applicationFormStatuses.INCOMPLETE:
                return <FormattedMessage id="applicantHomeEdit" />;
            case applicationFormStatuses.READONLY:
            case applicationFormStatuses.WITHDRAWN:
                return <FormattedMessage id="applicantHomeView" />;
            default:
                break;
        }
    }, [status, appIsConsideredSubmitted, archivedOn, enableDecision]);
    
    const onRespondNow = useCallback(() => {
        setIsDialogOpen(true);
    }, [isDialogOpen]);
    
    const onClose = useCallback(() => {
        setIsDialogOpen(false);
    }, [isDialogOpen]);
    
    const triggerConfetti = useCallback(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            confettiCreate(canvas, {
                resize: true,
            })({
                particleCount: isVerySmall ? 150 : 300,
                spread: isVerySmall ? 70 : 80,
                origin: { y: isVerySmall ? 1.5 : 3 },
            });
        }
    }, [isVerySmall, canvasRef]);

    const sendOfferStatus = useCallback(
        async (offerStatus) => {
            try {
                setIsOngoingApiCall(true);
                const response = await api.decisions.updateOfferStatusById(
                    application,
                    offerStatus,
                    applicantComment
                );
                application.offerStatus = response.data.offerStatus;
            } catch (e) {
                console.error(e);
                const error = await e;
                if (error.errorCode === 422) {
                    snackbar.error(
                        formatMessage({
                            id: 'applicantHomeSendOfferStatusWhenDecisionStatusChangedError',
                        })
                    );
                } else {
                    snackbar.error(
                        formatMessage({ id: 'applicantHomeSendOfferStatusError' })
                    );
                }
            } finally {
                setIsOngoingApiCall(false);
                onClose();
            }
        },
        [api, application, snackbar, onClose, applicantComment]
    );

    const onAccept = useCallback(async () => {
        await sendOfferStatus(CONSTANTS.applicationOfferStatus.accepted);
        //trigger confetti
        if (
            application.decisionStatus ===
        CONSTANTS.applicationDecisionStatus.admitted &&
        application.offerStatus === CONSTANTS.applicationOfferStatus.accepted
        ) {
            triggerConfetti();
        }
    }, [
        sendOfferStatus,
        triggerConfetti,
        application.decisionStatus,
        application.offerStatus,
    ]);

    const onDecline = useCallback(() => {
        setIsApplicantCommentDialogOpen(true);
    }, []);

    const onDeclineConfirmation = useCallback(async () => {
        await sendOfferStatus(CONSTANTS.applicationOfferStatus.declined);
        setIsApplicantCommentDialogOpen(false);
        setIsDialogOpen(false);
    }, [sendOfferStatus]);

    const onConditionsMet = useCallback(async () => {
        await sendOfferStatus(CONSTANTS.applicationOfferStatus.conditionsCompleted);
        setIsDialogOpen(false);
        snackbar.success(formatMessage({ id: 'applicantHomeConditionsMetSuccess' }));
    }, [sendOfferStatus]);

    const accessApplication = useCallback(
        () => accessApplicationProp(application),
        [accessApplicationProp, id, status]
    );
    //This is a data structure for designing the decision status attributes used in the application card
    const decisionStatusAttributes = {
        [CONSTANTS.applicationDecisionStatus.admitted]: {
            iconName: 'graduation',
            color: colorTextAlertSuccess,
            textKey: 'applicantHomeActionButtonAcceptOrRejectOffer',
            acceptedTextKey: 'applicantHomeOfferAccepted',
            declinedTextKey: 'applicantHomeOfferDeclined',
            statusLabelKey: 'applicantHomeDecisionAdmitted',
        },
        [CONSTANTS.applicationDecisionStatus.conditionalAdmit]: {
            iconName: 'list-view',
            color: colorTextAlertWarning,
            textKey: 'applicantHomeActionButtonViewConditionDetails',
            acceptedTextKey: 'applicantHomeConditionsAccepted',
            statusLabelKey: 'applicantHomeDecisionConditionalAdmit',
        },
        [CONSTANTS.applicationDecisionStatus.waitlist]: {
            iconName: 'clock',
            color: colorTextAlertWarning,
            textKey: 'applicantHomeActionButtonAcceptOrRejectWaitlist',
            acceptedTextKey: 'applicantHomeWaitlistAccepted',
            declinedTextKey: 'applicantHomeWaitlistDeclined',
            statusLabelKey: 'applicantHomeDecisionWaitlist',
        },
        [CONSTANTS.applicationDecisionStatus.declined]: {
            iconName: 'comments',
            color: colorTextNeutral500,
            statusLabelKey: 'applicantHomeDecisionDeclined',
        },
    };
    return (
        <Card>
            <CardContent className={classes.applicationCardContainer}>
                <canvas ref={canvasRef} className={classes.confetti} />
                <div className={classes.completedContainer}>
                    {application.decisionStatus || (appIsConsideredSubmitted && enableDecision) ? (
                        <div>
                            {application.decisionStatus ? (
                                <div className={classes.statusLabelItems}>
                                    <Typography className={classes.iconFontWeight}>
                              
                                        <Icon
                                            name={
                                                decisionStatusAttributes[application.decisionStatus] &&
                        decisionStatusAttributes[application.decisionStatus]
                          .iconName
                                            }
                                            style={{
                                                color:
                          decisionStatusAttributes[application.decisionStatus]
                            .color,
                                                fontWeight:'bolder'
                                            }}
                                            large
                                        />
                                    </Typography>
                                </div>
                            ) : (
                                <div>
                                    <Icon large name="check-feedback" className={classes.statusFont} />
                                </div>
                            )}
                        </div>
                    ) : (
                        <Typography variant="h1" component="span">
                            <FormattedMessage
                                id="applicantHomePagesCompleted"
                                values={{
                                    currentPage: completedCount.toLocaleString(globalLocale),
                                    totalPages: pageCount.toLocaleString(globalLocale),
                                }}
                            />
                        </Typography>
                    )}
                    <div className={classes.statusLabelItems}>
                        <Typography component="div" variant="body3">
                            {application.decisionStatus ? (
                                <Typography
                                    style={{
                                        color: decisionStatusAttributes[application?.decisionStatus].color,
                                        fontWeight:'bolder',
                                    }}
                                >
                                    {formatMessage({
                                        id: decisionStatusAttributes[application?.decisionStatus]?.statusLabelKey
                                    })}
                                </Typography>
                            ) : appIsConsideredSubmitted && enableDecision ? (
                                <Typography className={classes.submitStatus}>
                                    <FormattedMessage id="applicantHomeSubmitted" />  
                                </Typography>
                            ) : (
                                <div>
                                    <FormattedMessage id="applicantHomePages" />
                                    <Typography variant="body3"><FormattedMessage id="applicantHomeCompleted" /></Typography>
                                </div>
                            )}
                        </Typography>
                    </div>
                </div>
                <div className={classes.divider} />
                <div className={classes.infoContainer}>
                    <Typography
                        variant={isMobile ? 'h4' : 'h3'}
                        component="h2"
                        className={classes.standardField}
                    >
                        {getLocalizedName()}
                    </Typography>
                    <div className={classes.academicContainer}>
                        {academicTerm && (
                            <Typography variant="body2" className={classes.academicItems}>
                                {academicTerm}
                            </Typography>
                        )}
                        {academicProgram && (
                            <Typography variant="body2" className={classes.academicItems}>
                                {academicProgram}
                            </Typography>
                        )}
                    </div>
                    {isStacked ? (
                        isVerySmall ? (
                            <div className={classes.mobileStatusRow}>
                                <div className={classes.mobileStatusLeft}>
                                    {/*If decision is not enabled and app is not yet submitted we need to show the status format message.*/}
                                    {(!enableDecision || !appIsConsideredSubmitted) && (
                                        <Typography variant="body3" className={classes.infoitemLabel}>
                                            <FormattedMessage id="applicantHomeStatus" />
                                        </Typography>
                                    )}
                                    {!archivedOn && (
                                        <>
                                            {status === applicationFormStatuses.NOTSTARTED &&
                                                <StatusLabel text={formatMessage({ id: 'applicantHomeNotStarted' })} type="draft" />
                                            }
                                            {/*if the decision is not enabled and app is submitted we want to show the submitted status chip*/}
                                            {!enableDecision && appIsConsideredSubmitted ?
                                                <StatusLabel text={formatMessage({ id: 'appDesignResponsesSubmitted' })} type="success" />
                                                : <></>
                                            }
                                            {status === applicationFormStatuses.STARTED && (
                                                <StatusLabel
                                                    text={
                                                        paymentStatus &&
                                                        ![
                                                            applicationPaymentInformation.paymentStatus.draft,
                                                            applicationPaymentInformation.paymentStatus.successful,
                                                        ].includes(paymentStatus)
                                                            ? formatMessage({ id: 'applicantHomePaymentPending' })
                                                            : formatMessage({ id: 'applicantHomeInProgress' })
                                                    }
                                                    type="pending"
                                                />
                                            )}
                                            {status === applicationFormStatuses.READONLY && (
                                                <StatusLabel
                                                    text={formatMessage({ id: 'applicantHomeReadonly' })}
                                                />
                                            )}
                                            {status === applicationFormStatuses.WITHDRAWN && (
                                                <StatusLabel
                                                    text={formatMessage({ id: 'applicantHomeWithdrawn' })}
                                                />
                                            )}
                                            {status === applicationFormStatuses.INCOMPLETE && (
                                                <>
                                                    <StatusLabel
                                                        text={formatMessage({ id: 'applicantHomeIncomplete' })}
                                                        type="error"
                                                    />
                                                    <Tooltip title={formatMessage({ id: 'applicantHomeIncompleteTooltip' })} placement="top">
                                                        <Icon name="info" className={classes.incompleteInfoIcon} />
                                                    </Tooltip>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                                <Button
                                    className={classes.mobilePrimaryButton}
                                    onClick={accessApplication}
                                    variant={enableDecision && application.decisionStatus ? 'text' : undefined}
                                    color={enableDecision && !application.decisionStatus && appIsConsideredSubmitted ? 'secondary' : 'primary'}
                                >
                                    {getActionCardButtonName}
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className={classes.mobileStatusLeft}>
                                    {/*If decision is not enabled and app is not yet submitted we need to show the status format message.*/}
                                    {(!enableDecision || !appIsConsideredSubmitted) && (
                                        <Typography variant="body3" className={classes.infoitemLabel}>
                                            <FormattedMessage id="applicantHomeStatus" />
                                        </Typography>
                                    )}
                                    {!archivedOn && (
                                        <>
                                            {status === applicationFormStatuses.NOTSTARTED &&
                                                <StatusLabel text={formatMessage({ id: 'applicantHomeNotStarted' })} type="draft" />
                                            }
                                            {!enableDecision && appIsConsideredSubmitted ?
                                                <StatusLabel text={formatMessage({ id: 'appDesignResponsesSubmitted' })} type="success" />
                                                : <></>
                                            }
                                            {status === applicationFormStatuses.STARTED && (
                                                <StatusLabel
                                                    text={
                                                        paymentStatus &&
                                                        ![
                                                            applicationPaymentInformation.paymentStatus.draft,
                                                            applicationPaymentInformation.paymentStatus.successful,
                                                        ].includes(paymentStatus)
                                                            ? formatMessage({ id: 'applicantHomePaymentPending' })
                                                            : formatMessage({ id: 'applicantHomeInProgress' })
                                                    }
                                                    type="pending"
                                                />
                                            )}
                                            {status === applicationFormStatuses.READONLY && (
                                                <StatusLabel
                                                    text={formatMessage({ id: 'applicantHomeReadonly' })}
                                                />
                                            )}
                                            {status === applicationFormStatuses.WITHDRAWN && (
                                                <StatusLabel
                                                    text={formatMessage({ id: 'applicantHomeWithdrawn' })}
                                                />
                                            )}
                                            {status === applicationFormStatuses.INCOMPLETE && (
                                                <>
                                                    <StatusLabel
                                                        text={formatMessage({ id: 'applicantHomeIncomplete' })}
                                                        type="error"
                                                    />
                                                    <Tooltip title={formatMessage({ id: 'applicantHomeIncompleteTooltip' })} placement="top">
                                                        <Icon name="info" className={classes.incompleteInfoIcon} />
                                                    </Tooltip>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                                <Button
                                    className={classes.tabletPrimaryButton}
                                    onClick={accessApplication}
                                    variant={enableDecision && application.decisionStatus ? 'text' : undefined}
                                    color={enableDecision && !application.decisionStatus && appIsConsideredSubmitted ? 'secondary' : 'primary'}
                                >
                                    {getActionCardButtonName}
                                </Button>
                            </>
                        )
                    ) : (
                        <>
                            <div className={classes.desktopStatusRow}>
                                {/*If decision is not enabled and app is not yet submitted we need to show the status format message.*/}
                                {(!enableDecision || !appIsConsideredSubmitted) && (
                                    <Typography variant="body3" className={classes.infoitemLabel}>
                                        <FormattedMessage id="applicantHomeStatus" />
                                    </Typography>
                                )}
                                {!archivedOn && (
                                    <div className={classes.desktopStatusChips}>
                                        {status === applicationFormStatuses.NOTSTARTED &&
                                            <StatusLabel text={formatMessage({ id: 'applicantHomeNotStarted' })} type="draft" />
                                        }
                                        {/*if the decision is not enabled and app is submitted we want to show the submitted status chip*/}
                                        {!enableDecision && appIsConsideredSubmitted ?
                                            <StatusLabel text={formatMessage({ id: 'appDesignResponsesSubmitted' })} type="success" />
                                            : <></>
                                        }
                                        {status === applicationFormStatuses.STARTED && (
                                            <StatusLabel
                                                text={
                                                    paymentStatus &&
                                                        ![
                                                            applicationPaymentInformation.paymentStatus.draft,
                                                            applicationPaymentInformation.paymentStatus.successful,
                                                        ].includes(paymentStatus)
                                                        ? formatMessage({ id: 'applicantHomePaymentPending' })
                                                        : formatMessage({ id: 'applicantHomeInProgress' })
                                                }
                                                type="pending"
                                            />
                                        )}
                                        {status === applicationFormStatuses.READONLY && (
                                            <StatusLabel
                                                text={formatMessage({ id: 'applicantHomeReadonly' })}
                                            />
                                        )}
                                        {status === applicationFormStatuses.WITHDRAWN && (
                                            <StatusLabel
                                                text={formatMessage({ id: 'applicantHomeWithdrawn' })}
                                            />
                                        )}
                                        {status === applicationFormStatuses.INCOMPLETE && (
                                            <>
                                                <StatusLabel
                                                    text={formatMessage({ id: 'applicantHomeIncomplete' })}
                                                    type="error"
                                                />
                                                <Tooltip title={formatMessage({ id: 'applicantHomeIncompleteTooltip' })} placement="top">
                                                    <Icon name="info" className={classes.incompleteInfoIcon} />
                                                </Tooltip>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {!archivedOn && (
                                <>
                                    {application?.offerStatus === CONSTANTS.applicationOfferStatus.conditionsCompleted || (application.decisionStatus &&
                                        application.decisionStatus !==
                                            CONSTANTS.applicationDecisionStatus.declined && !application?.offerStatus) ? (
                                            <Button
                                                color="secondary"
                                                className={classes.buttonMargin}
                                                onClick={onRespondNow}
                                            >
                                                {formatMessage({ id: decisionStatusAttributes[application?.decisionStatus]?.textKey })}
                                            </Button>
                                        ) : (
                                            <></>
                                        )}
                                    {application.offerStatus &&
                                        <Typography variant="body2" color="textSecondary">
                                            {application.offerStatus === CONSTANTS.applicationOfferStatus.accepted || application.offerStatus === CONSTANTS.applicationOfferStatus.conditionsCompleted
                                                ? formatMessage({ id: decisionStatusAttributes[application.decisionStatus]?.acceptedTextKey })
                                                : formatMessage({ id: decisionStatusAttributes[application.decisionStatus]?.declinedTextKey})
                                            }
                                        </Typography>}
                                    {isDialogOpen && (
                                        <CallToActionDialog
                                            application={application}
                                            onClose={onClose}
                                            onAccept={onAccept}
                                            onDecline={onDecline}
                                            onConditionsMet={onConditionsMet}
                                            isOngoingApiCall={isOngoingApiCall}
                                            applicantComment={applicantComment}
                                            setApplicantComment={setApplicantComment}
                                        />
                                    )}
                                    {isApplicantCommentDialogOpen && (
                                        <ApplicantCommentDialog
                                            application={application}
                                            onClose={()=>setIsApplicantCommentDialogOpen(false)}
                                            onDeclineConfirmation={onDeclineConfirmation}
                                            isOngoingApiCall={isOngoingApiCall}
                                            applicantComment={applicantComment}
                                            setApplicantComment={setApplicantComment}
                                        />
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>

                {!isStacked && (
                    <div className={classes.buttonContainer}>
                        <Button
                            className={classes.button}
                            onClick={accessApplication}
                            variant={enableDecision && application.decisionStatus ? 'text' : undefined}
                            color={enableDecision && !application.decisionStatus && appIsConsideredSubmitted ? 'secondary' : 'primary'}
                        >
                            {getActionCardButtonName}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
export default ApplicationCard;
